import React, { useState } from 'react';

const NON_EQUIL_TESTS = [
  { id: 'finger_nose',    label: 'Finger to Nose',          findings: ['Dysmetria','Past-pointing','Tremor','Slow','Normal'] },
  { id: 'finger_opp',    label: 'Finger Opposition',        findings: ['Impaired','Dysdiadochokinesia','Slow','Normal'] },
  { id: 'mass_grasp',    label: 'Mass Grasp',               findings: ['Weak','Poorly graded','Slow','Normal'] },
  { id: 'pron_sup',      label: 'Pronation / Supination',   findings: ['Dysdiadochokinesia','Irregular rhythm','Slow','Normal'] },
  { id: 'rebound',       label: 'Rebound Test',             findings: ['Positive (no check)','Partial check','Normal'] },
  { id: 'tapping_hand',  label: 'Tapping (Hand)',           findings: ['Irregular rhythm','Slow','Dysrhythmic','Normal'] },
  { id: 'tapping_foot',  label: 'Tapping (Foot)',           findings: ['Irregular rhythm','Slow','Dysrhythmic','Normal'] },
  { id: 'heel_knee',     label: 'Heel to Knee',             findings: ['Dysmetria','Tremor','Ataxic','Slow','Normal'] },
  { id: 'circle_hand',   label: 'Drawing Circle (Hand)',    findings: ['Irregular','Dysmetric','Tremor','Normal'] },
  { id: 'circle_foot',   label: 'Drawing Circle (Foot)',    findings: ['Irregular','Dysmetric','Tremor','Normal'] },
];

const EQUIL_TESTS = [
  { id: 'stand_normal',     label: 'Standing: Normal Posture' },
  { id: 'stand_vision',     label: 'Standing: Vision Occluded (Romberg)' },
  { id: 'stand_feet',       label: 'Standing: Feet Together' },
  { id: 'stand_one_foot',   label: 'Standing on One Foot' },
  { id: 'stand_lat_flex',   label: 'Standing: Lateral Trunk Flexion' },
  { id: 'tandem_walk',      label: 'Tandem Walking' },
  { id: 'walk_sideways',    label: 'Walk: Sideways' },
  { id: 'walk_backward',    label: 'Walk: Backward' },
  { id: 'walk_circle',      label: 'Walk in a Circle' },
  { id: 'walk_heels',       label: 'Walk on Heels' },
  { id: 'walk_toes',        label: 'Walk on Toes' },
];

const EQUIL_GRADES = [
  { id: 'N',  label: 'Normal',   color: '#059669', bg: '#ecfdf5', border: '#6ee7b7' },
  { id: 'I',  label: 'Impaired', color: '#d97706', bg: '#fffbeb', border: '#fcd34d' },
  { id: 'U',  label: 'Unable',   color: '#dc2626', bg: '#fef2f2', border: '#fca5a5' },
  { id: 'NT', label: 'NT',       color: '#64748b', bg: '#f1f5f9', border: '#cbd5e1' },
];

const CoordinationAssessment = ({ value = {}, onChange }) => {
  const [open, setOpen] = useState(false);

  const get = (key) => value[key] || null;
  const set = (key, val) => onChange?.({ ...value, [key]: value[key] === val ? null : val });
  const toggle = (key, finding) => {
    const curr = value[key] || [];
    const updated = curr.includes(finding) ? curr.filter(f => f !== finding) : [...curr, finding];
    onChange?.({ ...value, [key]: updated });
  };

  const nonEquilAbnormal = NON_EQUIL_TESTS.filter(t =>
    ['rt','lt'].some(s => { const v = value[`${t.id}_${s}`] || []; return v.length > 0 && !v.every(f => f === 'Normal'); })
  ).length;
  const equilAbnormal = EQUIL_TESTS.filter(t => {
    const rt = get(`equil_${t.id}_rt`);
    const lt = get(`equil_${t.id}_lt`);
    return (rt && rt !== 'N' && rt !== 'NT') || (lt && lt !== 'N' && lt !== 'NT');
  }).length;
  const totalAbnormal = nonEquilAbnormal + equilAbnormal;

  return (
    <div style={{ marginBottom: '8px' }}>
      {!open ? (
        <button type="button" onClick={() => setOpen(true)} style={{
          width: '100%', padding: '15px',
          borderRadius: '14px', border: '1px solid #e2e8f0',
          backgroundColor: 'transparent', color: '#0e7490',
          fontSize: '0.95rem', fontWeight: '700', cursor: 'pointer',
          display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px',
          transition: 'all 0.2s'
        }}>
          + Coordination Assessment
          {totalAbnormal > 0 && (
            <span style={{ fontSize: '11px', padding: '2px 10px', borderRadius: '20px', background: '#0e7490', color: '#fff', fontWeight: 800 }}>
              {nonEquilAbnormal} non-equil · {equilAbnormal} equil abnormal
            </span>
          )}
        </button>
      ) : (
        <div style={{ padding: '20px' }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
            <span style={{ fontWeight: '800', fontSize: '1rem', color: '#0e7490' }}>🎯 Coordination Assessment</span>
            <button type="button" onClick={() => setOpen(false)}
              style={{ background: 'none', border: 'none', color: '#cbd5e1', fontSize: '1.6rem', cursor: 'pointer', lineHeight: 1 }}
              onMouseEnter={e => e.target.style.color = '#ef4444'}
              onMouseLeave={e => e.target.style.color = '#cbd5e1'}>×</button>
          </div>

          {/* Summary bar */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '12px',
            padding: '10px 16px', borderRadius: '12px',
            background: totalAbnormal > 0 ? '#fef2f2' : '#f0fdf4',
            border: `1px solid ${totalAbnormal > 0 ? '#fca5a5' : '#6ee7b7'}`,
            marginBottom: '14px'
          }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: totalAbnormal > 0 ? '#dc2626' : '#059669' }}>{totalAbnormal}</div>
            <div>
              <div style={{ fontSize: '13px', fontWeight: 700, color: totalAbnormal > 0 ? '#991b1b' : '#065f46' }}>
                {totalAbnormal === 0 ? 'No coordination deficits documented yet' : `${totalAbnormal} test${totalAbnormal > 1 ? 's' : ''} with abnormal findings`}
              </div>
              <div style={{ fontSize: '11px', color: '#94a3b8' }}>Select findings per side for non-equilibrium tests · Grade equilibrium tests</div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px' }}>
            {/* NON-EQUILIBRIUM TESTS */}
            <div style={{ border: '0.5px solid #bfdbfe', borderRadius: '12px', overflow: 'hidden' }}>
              <div style={{ background: '#eff6ff', padding: '8px 14px', borderBottom: '0.5px solid #bfdbfe' }}>
                <div style={{ fontSize: '11px', fontWeight: 800, color: '#1e3a8a', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>Non-Equilibrium Tests</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                  <div style={{ fontSize: '10px', fontWeight: 700, color: '#1e3a8a', textAlign: 'center' }}>Right (RL)</div>
                  <div style={{ fontSize: '10px', fontWeight: 700, color: '#1e3a8a', textAlign: 'center' }}>Left (LL)</div>
                </div>
              </div>
              {NON_EQUIL_TESTS.map((t, idx) => {
                const rtFindings = value[`${t.id}_rt`] || [];
                const ltFindings = value[`${t.id}_lt`] || [];
                const rtAbnormal = rtFindings.length > 0 && !rtFindings.every(f => f === 'Normal');
                const ltAbnormal = ltFindings.length > 0 && !ltFindings.every(f => f === 'Normal');
                return (
                  <div key={t.id} style={{
                    borderBottom: idx < NON_EQUIL_TESTS.length - 1 ? '0.5px solid #f1f5f9' : 'none',
                    background: (rtAbnormal || ltAbnormal) ? '#fafcff' : '#fff'
                  }}>
                    <div style={{ padding: '7px 10px', fontSize: '11px', fontWeight: 700, color: '#374151', borderBottom: '0.5px solid #f8fafc' }}>
                      {t.label}
                      {(rtAbnormal || ltAbnormal) && <span style={{ marginLeft: '6px', fontSize: '10px', color: '#dc2626', fontWeight: 700 }}>⚠</span>}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0' }}>
                      {['rt','lt'].map((side, si) => {
                        const selected = value[`${t.id}_${side}`] || [];
                        return (
                          <div key={side} style={{ padding: '5px 8px', borderRight: si === 0 ? '0.5px solid #f1f5f9' : 'none', display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
                            {t.findings.map(f => {
                              const active = selected.includes(f);
                              const isNormal = f === 'Normal';
                              return (
                                <button key={f} type="button" onClick={() => toggle(`${t.id}_${side}`, f)} style={{
                                  fontSize: '10px', fontWeight: 600, padding: '3px 7px', borderRadius: '20px', cursor: 'pointer',
                                  transition: 'all 0.15s',
                                  border: `1px solid ${active ? (isNormal ? '#6ee7b7' : '#fca5a5') : '#e2e8f0'}`,
                                  background: active ? (isNormal ? '#ecfdf5' : '#fef2f2') : 'transparent',
                                  color: active ? (isNormal ? '#059669' : '#dc2626') : '#64748b'
                                }}>{active ? '✓ ' : ''}{f}</button>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* EQUILIBRIUM TESTS */}
            <div style={{ border: '0.5px solid #d9f99d', borderRadius: '12px', overflow: 'hidden' }}>
              <div style={{ background: '#f7fee7', padding: '8px 14px', borderBottom: '0.5px solid #d9f99d' }}>
                <div style={{ fontSize: '11px', fontWeight: 800, color: '#365314', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>Equilibrium Tests</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr' }}>
                  <div />
                  <div style={{ fontSize: '10px', fontWeight: 700, color: '#365314', textAlign: 'center' }}>Right</div>
                  <div style={{ fontSize: '10px', fontWeight: 700, color: '#365314', textAlign: 'center' }}>Left</div>
                </div>
              </div>
              {EQUIL_TESTS.map((t, idx) => {
                const rtGrade = get(`equil_${t.id}_rt`);
                const ltGrade = get(`equil_${t.id}_lt`);
                const isAbnormal = (rtGrade && rtGrade !== 'N' && rtGrade !== 'NT') || (ltGrade && ltGrade !== 'N' && ltGrade !== 'NT');
                return (
                  <div key={t.id} style={{
                    display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
                    alignItems: 'start', padding: '8px 10px',
                    borderBottom: idx < EQUIL_TESTS.length - 1 ? '0.5px solid #f1f5f9' : 'none',
                    background: isAbnormal ? '#fffef0' : '#fff'
                  }}>
                    <div style={{ fontSize: '11px', fontWeight: 600, color: '#374151', paddingTop: '3px' }}>{t.label}</div>
                    {['rt', 'lt'].map(side => {
                      const grade = get(`equil_${t.id}_${side}`);
                      return (
                        <div key={side} style={{ display: 'flex', gap: '3px', justifyContent: 'center', flexWrap: 'wrap' }}>
                          {EQUIL_GRADES.map(g => {
                            const active = grade === g.id;
                            return (
                              <button key={g.id} type="button" onClick={() => set(`equil_${t.id}_${side}`, g.id)} style={{
                                fontSize: '10px', fontWeight: 700, padding: '3px 6px', borderRadius: '6px', cursor: 'pointer',
                                transition: 'all 0.15s',
                                border: `1px solid ${active ? g.border : '#e2e8f0'}`,
                                background: active ? g.bg : 'transparent',
                                color: active ? g.color : '#94a3b8'
                              }}>{g.label}</button>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>

          {Object.keys(value).length > 0 && (
            <button type="button" onClick={() => onChange({})} style={{ marginTop: '10px', fontSize: '12px', background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', textDecoration: 'underline' }}>
              Clear all coordination data
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default CoordinationAssessment;
